/**
 * DealModal — criar/editar um Deal (card do funil) e anexar Leads (deal_contacts).
 *
 * DEAL-CENTRIC: o Deal é a oportunidade; os Leads (contacts) pertencem a ele via
 * deal_contacts (M:N), com 1 lead primário. Responsável (owner_id) via /members.
 * Ao marcar Perdido, pede motivo (loss_reasons) + justificativa; o backend
 * carimba data_fechamento/won_time/lost_time conforme o status.
 *
 * Visual: mesmo drawer lateral do módulo Financeiro (fc-drawer-*, "Nova
 * despesa/receita" em ../financeiro/contas.css) — cartão branco, borda
 * #e4e7ec, azul #2563eb. Peças específicas (chip de valor, linha de lead,
 * histórico) em pm-* (./pipelines.css).
 *
 * Props:
 *   dealId        — id do deal p/ editar; null = criar novo
 *   pipelineId    — funil onde o card nasce (create)
 *   stageId       — etapa inicial (create); null = sem etapa
 *   initialContact— {id, nome} p/ pré-preencher o título e já anexar o lead
 *                   ao criar (usado pelo "Converter em negócio" do LeadPanel)
 *   onClose()     — fecha sem mudar
 *   onSaved()     — fecha e pede reload do board
 */

import { useEffect, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { ItemsTab, ConfirmDialog } from './panelKit.jsx'
import { fmtBRL } from '../../db/api.js'
import {
  getDeal,
  createDeal,
  updateDeal,
  deleteDeal,
  getDealContacts,
  linkDealContact,
  unlinkDealContact,
  updateDealContact,
  getCrmContacts,
  createCrmContact,
  getDealTransitions,
  getLossReasons,
  createLossReason,
  getPipelines,
  getStages,
  getCrmMembers,
  getCompany,
  getCompanies,
  moveDeal,
  searchEmpresasPublicas,
  importarEmpresas,
} from '../../db/api.js'
import '../financeiro/contas.css'
import './pipelines.css'
import DealCreatePanel, { SearchPicker, fmtCnpj, SITUACAO } from './DealCreatePanel.jsx'

export default function DealModal(props) {
  return props.dealId ? <ExistingDealModal {...props} /> : <DealCreatePanel {...props} />
}

function ExistingDealModal({ dealId, pipelineId, stageId, initialContact, onClose, onSaved, onWon }) {
  const isEdit = !!dealId
  const [id, setId] = useState(dealId || null)
  const initialStatusRef = useRef('aberto')   // status na carga — detecta virada p/ ganho
  const companyRef = useRef(null)             // company_id do deal (prefill do projeto)
  const [titulo, setTitulo] = useState(initialContact?.nome || '')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState('aberto')
  const [ownerId, setOwnerId] = useState('')
  const [members, setMembers] = useState([])
  const [company, setCompany] = useState(null)       // empresa do negócio (edição)
  const [companyMode, setCompanyMode] = useState('select') // select | cnpj
  const [cnpjQuery, setCnpjQuery] = useState('')
  const [cnpjFound, setCnpjFound] = useState(null)
  const [cnpjBusy, setCnpjBusy] = useState(false)
  const [cnpjError, setCnpjError] = useState('')
  const [editStages, setEditStages] = useState([])   // etapas do funil escolhido (edição)
  const [dealPipelineId, setDealPipelineId] = useState('')
  const [dealStageId, setDealStageId] = useState('')
  const initialStageRef = useRef('')
  // Seletor de pipeline na criação. Etapa = primeira do pipeline escolhido
  // (a etapa do contexto é preservada enquanto o pipeline não muda).
  const [pipelines, setPipelines] = useState([])
  const [pipelineIdSel, setPipelineIdSel] = useState(pipelineId || '')
  const [stageIdSel, setStageIdSel] = useState(stageId || null)
  const [lossReasons, setLossReasons] = useState([])
  const [lossReasonId, setLossReasonId] = useState('')
  const [novaLoss, setNovaLoss] = useState('')       // criar motivo novo na hora
  const [justificativa, setJustificativa] = useState('')
  const [transitions, setTransitions] = useState([])
  const [itemsTotal, setItemsTotal] = useState(0)   // somatório dos deal_items
  const [contacts, setContacts] = useState([])      // leads já anexados
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // busca de leads p/ anexar
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [creating, setCreating] = useState(false)

  // ── motivos de perda ──────────────────────────────────────────────────
  useEffect(() => {
    let alive = true
    getLossReasons().then((r) => alive && setLossReasons(r)).catch(() => {})
    return () => { alive = false }
  }, [])

  // ── pipelines (só na criação) — permite escolher onde o negócio nasce ──
  useEffect(() => {
    if (isEdit) return
    let alive = true
    getPipelines().then((ps) => {
      if (!alive) return
      setPipelines(ps)
      // sem pipeline do contexto: usa o padrão (ou o primeiro)
      if (!pipelineId && ps.length) {
        const def = ps.find((p) => p.is_default) || ps[0]
        setPipelineIdSel(def.id)
        getStages(def.id).then((st) => alive && setStageIdSel(st[0]?.id || null)).catch(() => {})
      }
    }).catch(() => {})
    return () => { alive = false }
  }, [isEdit, pipelineId])

  // troca de pipeline → etapa vira a primeira do pipeline escolhido
  const handlePipelineChange = async (pid) => {
    setPipelineIdSel(pid)
    try { const st = await getStages(pid); setStageIdSel(st[0]?.id || null) }
    catch { setStageIdSel(null) }
  }

  // ── carga (modo edição) ───────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return
    let alive = true
    ;(async () => {
      try {
        const [d, cs, tr] = await Promise.all([
          getDeal(id), getDealContacts(id), getDealTransitions(id),
        ])
        if (!alive) return
        setTitulo(d.titulo || '')
        setItemsTotal(d.valor != null ? Number(d.valor) : 0) // valor = Σ itens (backend)
        setDescription(d.description || '')
        setStatus(d.status || 'aberto')
        initialStatusRef.current = d.status || 'aberto'
        companyRef.current = d.company_id || null
        initialStageRef.current = d.stage_id || ''
        setDealPipelineId(d.pipeline_id || '')
        setDealStageId(d.stage_id || '')
        if (d.pipeline_id) getStages(d.pipeline_id).then((st) => alive && setEditStages(st)).catch(() => {})
        if (d.company_id) getCompany(d.company_id).then((c) => alive && setCompany(c)).catch(() => {})
        setOwnerId(d.owner_id || '')
        setLossReasonId(d.loss_reason_id || '')
        setJustificativa(d.justificativa || '')
        setContacts(cs)
        setTransitions(tr)
      } catch (e) {
        if (alive) setErr(e.message)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [isEdit, id])

  // membros + pipelines para a edição completa (responsável, funil e etapa)
  useEffect(() => {
    if (!isEdit) return
    let alive = true
    getCrmMembers().then((m) => alive && setMembers(m)).catch(() => {})
    getPipelines().then((ps) => alive && setPipelines(ps)).catch(() => {})
    return () => { alive = false }
  }, [isEdit])

  const handleEditPipelineChange = async (pid) => {
    setDealPipelineId(pid)
    try {
      const st = await getStages(pid)
      setEditStages(st)
      setDealStageId(st.find((s) => !s.is_ganho && !s.is_perdido)?.id || st[0]?.id || '')
    } catch { setEditStages([]); setDealStageId('') }
  }

  // Buscar CNPJ (mesmo fluxo do painel do negócio): consulta a base pública e importa.
  useEffect(() => {
    setCnpjFound(null); setCnpjError('')
    if (companyMode !== 'cnpj' || cnpjQuery.length !== 14) return
    let alive = true
    setCnpjBusy(true)
    searchEmpresasPublicas({ cnpj: cnpjQuery })
      .then((data) => {
        if (!alive) return
        if (data.items?.[0]) setCnpjFound(data.items[0])
        else setCnpjError('CNPJ não encontrado na base pública.')
      })
      .catch((e) => { if (alive) setCnpjError(e.status === 503 ? 'Base pública de CNPJ indisponível no momento.' : e.message) })
      .finally(() => { if (alive) setCnpjBusy(false) })
    return () => { alive = false }
  }, [companyMode, cnpjQuery])

  const importFoundCompany = async () => {
    if (!cnpjFound || cnpjBusy) return
    setCnpjBusy(true); setCnpjError('')
    try {
      const { imported } = await importarEmpresas([cnpjFound.cnpj])
      if (!imported?.[0]) throw new Error('Não foi possível importar a empresa.')
      setCompany(imported[0]); setCompanyMode('select'); setCnpjQuery(''); setCnpjFound(null)
    } catch (e) { setCnpjError(e.message) }
    finally { setCnpjBusy(false) }
  }

  // ── salvar dados do deal ──────────────────────────────────────────────
  // Ramifica por `id` (estado), NÃO por isEdit (prop). Após criar, `id` existe
  // e o próximo "Salvar" faz UPDATE — evita criar um 2º registro.
  const handleSave = async () => {
    if (saving) return                       // trava clique duplo
    if (!titulo.trim()) { setErr('Título é obrigatório.'); return }
    setSaving(true); setErr(null)
    // valor NÃO vai no payload: é derivado dos itens (deals.valor sincronizado
    // no backend a cada mudança de deal_item). Fonte única da verdade = itens.
    const payload = {
      titulo: titulo.trim(),
      description: description.trim() || null,
      owner_id: ownerId || null,
      ...(isEdit && (company?.id || null) !== companyRef.current ? { company_id: company?.id || null } : {}),
    }
    try {
      if (id) {
        // ao marcar Perdido, anexa motivo/justificativa (cria o motivo se for novo).
        const extra = {}
        const stageChanged = isEdit && dealStageId && dealStageId !== initialStageRef.current
        const destStage = stageChanged ? editStages.find((s) => s.id === dealStageId) : null
        const statusChanged = status !== initialStatusRef.current
        if (status === 'perdido' || destStage?.is_perdido) {
          let lrid = lossReasonId || null
          const nome = novaLoss.trim()
          if (!lrid && nome) {
            const created = await createLossReason(nome)
            lrid = created.id
            setLossReasons((p) => [...p, created])
            setLossReasonId(created.id); setNovaLoss('')
          }
          extra.loss_reason_id = lrid
          extra.justificativa = justificativa.trim() || null
        }
        if (stageChanged) {
          await moveDeal(id, {
            toStageId: dealStageId,
            ...(destStage?.is_perdido ? { lossReasonId: extra.loss_reason_id || null, justificativa: extra.justificativa || null } : {}),
          })
          initialStageRef.current = dealStageId
        }
        await updateDeal(id, { ...payload, ...(statusChanged || !stageChanged ? { status } : {}), ...extra })
        // virou ganho agora? oferece criar projeto (o pai abre o WinProjectModal)
        if ((status === 'ganho' || destStage?.is_ganho) && initialStatusRef.current !== 'ganho' && onWon) {
          onWon({ id, titulo: titulo.trim(), company_id: companyRef.current })
        }
        onSaved()                            // fecha + recarrega board
      } else {
        const d = await createDeal({
          ...payload,
          pipeline_id: pipelineIdSel || pipelineId,
          stage_id: stageIdSel ?? stageId,
        })
        // "Converter em negócio": já anexa o lead de origem como principal.
        if (initialContact?.id) {
          try {
            await linkDealContact(d.id, { contactId: initialContact.id, isPrimary: true })
            setContacts(await getDealContacts(d.id))
          } catch { /* segue mesmo se o vínculo falhar; usuário pode anexar manualmente */ }
        }
        setId(d.id)                          // agora é edição: permite anexar leads
        setLoading(false)
        // não fecha: deixa anexar leads antes de concluir.
      }
    } catch (e) {
      setErr(e.message)
    } finally {
      setSaving(false)
    }
  }

  // ── excluir o negócio (soft delete no backend) ────────────────────────
  const handleDelete = async () => {
    if (!id) return
    setDeleting(true); setErr(null)
    try {
      await deleteDeal(id)
      onSaved()
    } catch (e) {
      setErr(e.message); setDeleting(false); setDeleteOpen(false)
    }
  }

  // ── busca de leads ────────────────────────────────────────────────────
  const handleSearch = async (term) => {
    setQ(term)
    if (!term.trim()) { setResults([]); return }
    setSearching(true)
    try {
      const all = await getCrmContacts(term.trim())
      const linkedIds = new Set(contacts.map((c) => c.contact_id))
      setResults(all.filter((c) => !linkedIds.has(c.id)).slice(0, 8))
    } catch (e) {
      setErr(e.message)
    } finally {
      setSearching(false)
    }
  }

  // Fechar: se um deal foi criado/editado neste modal (id existe), recarrega o
  // board; senão só fecha. Evita card recém-criado não aparecer sem refresh.
  const closeSmart = () => (id ? onSaved() : onClose())

  const refreshContacts = async () => setContacts(await getDealContacts(id))

  const handleLink = async (contact) => {
    try {
      await linkDealContact(id, { contactId: contact.id, isPrimary: contacts.length === 0 })
      setQ(''); setResults([])
      await refreshContacts()
    } catch (e) { setErr(e.message) }
  }

  // cria um lead novo (contacts) a partir do termo digitado e já anexa ao deal.
  const handleCreateAndLink = async () => {
    const nome = q.trim()
    if (!nome || creating) return
    setCreating(true); setErr(null)
    try {
      const novo = await createCrmContact({ nome })
      await linkDealContact(id, { contactId: novo.id, isPrimary: contacts.length === 0 })
      setQ(''); setResults([])
      await refreshContacts()
    } catch (e) {
      setErr(e.message)
    } finally {
      setCreating(false)
    }
  }

  const handleSetPrimary = async (contactId) => {
    try {
      await updateDealContact(id, contactId, { is_primary: true })
      await refreshContacts()
    } catch (e) { setErr(e.message) }
  }

  const handleUnlink = async (contactId) => {
    try {
      await unlinkDealContact(id, contactId)
      await refreshContacts()
    } catch (e) { setErr(e.message) }
  }

  return (
    <div className="fc-drawer-overlay" onMouseDown={closeSmart}>
      <div className="fc-drawer" style={{ width: 560 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-drawer-head">
          <span className="fc-drawer-title">{id ? 'Editar negócio' : 'Novo negócio'}</span>
          <button className="fc-drawer-x" onClick={closeSmart}><Icon n="x" s={16} sw={2} /></button>
        </div>

        <div className="fc-drawer-body">
          {err && <div className="fc-error">{err}</div>}
          {loading ? (
            <div style={{ color: '#667085', fontSize: 13, padding: '4px 0' }}>Carregando…</div>
          ) : (
            <>
              {/* Dados do deal */}
              <div className="fc-field">
                <label className="fc-label">Título<i>*</i></label>
                <input className="fc-input" value={titulo} onChange={(e) => setTitulo(e.target.value)} autoFocus />
              </div>

              {/* Pipeline: escolhido na criação (etapa = primeira do pipeline). */}
              {!isEdit && !id && (
                <div className="fc-field">
                  <label className="fc-label">Pipeline</label>
                  <select
                    className="fc-select"
                    value={pipelineIdSel}
                    onChange={(e) => handlePipelineChange(e.target.value)}
                  >
                    {!pipelineIdSel && <option value="">— selecione —</option>}
                    {pipelines.map((p) => (
                      <option key={p.id} value={p.id}>{p.nome}{p.is_default ? ' (padrão)' : ''}</option>
                    ))}
                  </select>
                </div>
              )}

              {isEdit && (
                <div className="fc-field" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label className="fc-label">Pipeline</label>
                    <select className="fc-select" value={dealPipelineId} onChange={(e) => handleEditPipelineChange(e.target.value)}>
                      {pipelines.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="fc-label">Etapa</label>
                    <select className="fc-select" value={dealStageId} onChange={(e) => setDealStageId(e.target.value)}>
                      {!dealStageId && <option value="">— selecione —</option>}
                      {editStages.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Valor removido do topo: é calculado pelos itens e mostrado no
                  resumo (cabeçalho dos itens + rodapé). Status só na edição. */}
              {(isEdit || id) && (
                <div className="fc-field">
                  <label className="fc-label">Status</label>
                  <select className="fc-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="aberto">Aberto</option>
                    <option value="ganho">Ganho</option>
                    <option value="perdido">Perdido</option>
                  </select>
                </div>
              )}

              {/* Motivo da perda — só quando o negócio é marcado como Perdido */}
              {(isEdit || id) && (status === 'perdido' || editStages.find((s) => s.id === dealStageId)?.is_perdido) && (
                <div className="pm-danger-zone">
                  <div className="fc-field" style={{ marginBottom: 10 }}>
                    <label className="fc-label">Motivo da perda</label>
                    <select
                      className="fc-select"
                      value={lossReasonId}
                      onChange={(e) => { setLossReasonId(e.target.value); setNovaLoss('') }}
                    >
                      <option value="">— selecione —</option>
                      {lossReasons.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
                    </select>
                  </div>
                  {!lossReasonId && (
                    <div className="fc-field" style={{ marginBottom: 10 }}>
                      <input
                        className="fc-input"
                        value={novaLoss}
                        onChange={(e) => setNovaLoss(e.target.value)}
                        placeholder="ou crie um novo motivo…"
                      />
                    </div>
                  )}
                  <textarea
                    className="fc-input"
                    style={{ height: 'auto', minHeight: 56, padding: '10px 12px', resize: 'vertical' }}
                    value={justificativa}
                    onChange={(e) => setJustificativa(e.target.value)}
                    placeholder="Justificativa (opcional)"
                  />
                </div>
              )}

              <div className="fc-field">
                <label className="fc-label">Responsável</label>
                <select className="fc-select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                  <option value="">Sem responsável</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.nome || 'Usuário sem nome'}</option>)}
                </select>
              </div>

              {isEdit && (
                <section className="dc-section"><h3>Empresa</h3>
                  <div className="dc-toggle">
                    <button type="button" aria-pressed={companyMode === 'select'} onClick={() => setCompanyMode('select')}>Selecionar empresa</button>
                    <button type="button" aria-pressed={companyMode === 'cnpj'} onClick={() => setCompanyMode('cnpj')}>Buscar CNPJ</button>
                  </div>
                  {companyMode === 'select' ? (
                    <SearchPicker browseOnFocus label="Empresa" value={company} onChange={setCompany} search={getCompanies} placeholder="Buscar por nome ou CNPJ…" />
                  ) : (
                    <div className="fc-field dc-cnpj-search">
                      <label className="fc-label" htmlFor="dm-cnpj-search">CNPJ</label>
                      <input id="dm-cnpj-search" className="fc-input" inputMode="numeric" placeholder="Digite o CNPJ (só números)" maxLength={18} value={fmtCnpj(cnpjQuery)} onChange={(e) => setCnpjQuery(e.target.value.replace(/\D/g, '').slice(0, 14))} />
                      <div className="dc-cnpj-result" role="status">
                        {cnpjQuery.length < 14 ? <small>Digite os 14 dígitos do CNPJ.</small> : cnpjBusy && !cnpjFound ? <small>Buscando na base pública…</small> : cnpjFound ? (
                          <div className="dc-cnpj-found">
                            <strong>{cnpjFound.nome_fantasia || cnpjFound.razao_social}</strong>
                            {cnpjFound.nome_fantasia && <small>{cnpjFound.razao_social}</small>}
                            <small>{[cnpjFound.cidade, cnpjFound.uf].filter(Boolean).join(' / ')}{cnpjFound.situacao_cadastral ? ` · ${SITUACAO[cnpjFound.situacao_cadastral] || cnpjFound.situacao_cadastral}` : ''}{cnpjFound.already_imported ? ' · já está na organização' : ''}</small>
                            <button type="button" disabled={cnpjBusy} onClick={importFoundCompany}>{cnpjBusy ? 'Importando…' : cnpjFound.already_imported ? 'Usar esta empresa' : 'Importar e usar esta empresa'}</button>
                          </div>
                        ) : null}
                      </div>
                      {cnpjError && <p className="fc-error" role="alert">{cnpjError}</p>}
                    </div>
                  )}
                </section>
              )}

              <div className="fc-field">
                <label className="fc-label">Descrição</label>
                <textarea className="fc-input" style={{ height: 'auto', minHeight: 64, padding: '10px 12px', resize: 'vertical' }}
                  value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>

              {/* Leads anexados (só após o deal existir) */}
              {(isEdit || id) && (
                <div className="pm-section">
                  <div className="pm-section-title" style={{ marginBottom: 10 }}>Leads do negócio</div>
                  {!contacts.length && <div style={{ color: '#98a2b3', fontSize: 13 }}>Nenhum lead anexado.</div>}
                  {contacts.map((c) => (
                    <div key={c.contact_id} className="pm-list-row">
                      <div>
                        <span className="pm-list-name">{c.nome || 'Sem nome'}</span>
                        {c.is_primary && <span className="pm-list-primary-tag">principal</span>}
                        {(c.email || c.telefone) && <div className="pm-list-sub">{c.email || c.telefone}</div>}
                      </div>
                      <div className="pm-list-actions">
                        {!c.is_primary && (
                          <button className="pm-list-linkbtn" onClick={() => handleSetPrimary(c.contact_id)}>tornar principal</button>
                        )}
                        <button className="pm-list-removebtn" onClick={() => handleUnlink(c.contact_id)}>remover</button>
                      </div>
                    </div>
                  ))}

                  {/* Busca p/ anexar */}
                  <input className="fc-input" style={{ marginTop: 10 }} value={q}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Buscar lead por nome/email/telefone…" />
                  {searching && <div style={{ color: '#98a2b3', fontSize: 12.5, marginTop: 6 }}>Buscando…</div>}
                  {results.map((r) => (
                    <div key={r.id} className="pm-result-row" onClick={() => handleLink(r)}>
                      <span className="pm-list-name">{r.nome || 'Sem nome'}</span>
                      {(r.email || r.telefone) && <span className="pm-list-sub"> · {r.email || r.telefone}</span>}
                      <span className="pm-add-hint">+ anexar</span>
                    </div>
                  ))}
                  {q.trim() && !searching &&
                    !results.some((r) => (r.nome || '').toLowerCase() === q.trim().toLowerCase()) && (
                    <div className="pm-result-row create" onClick={handleCreateAndLink}>
                      <span className="pm-list-name">{creating ? 'Criando…' : `Criar lead "${q.trim()}"`}</span>
                      <span className="pm-add-hint">+ novo</span>
                    </div>
                  )}
                </div>
              )}

              {/* Itens do negócio (deal_items — produtos/serviços vendidos) */}
              {(isEdit || id) && (
                <div className="pm-section">
                  <div className="pm-section-head">
                    <span className="pm-section-title">Itens do negócio</span>
                    <span className="pm-value-chip" title="Valor do negócio = soma dos itens">
                      {fmtBRL(itemsTotal)}
                    </span>
                  </div>
                  <ItemsTab dealId={id} onTotalChange={setItemsTotal} />
                </div>
              )}

              {/* Histórico de etapas */}
              {(isEdit || id) && !!transitions.length && (
                <div className="pm-section">
                  <div className="pm-section-title" style={{ marginBottom: 6 }}>Histórico do funil</div>
                  {transitions.map((t) => (
                    <div key={t.id} className="pm-hist-row">
                      <span style={{ color: '#98a2b3' }}>
                        {new Date(t.occurred_at).toLocaleString('pt-BR')}
                      </span>
                      <span className="pm-list-name">
                        {t.from_nome || '—'} → {t.to_nome || '—'}
                      </span>
                      {t.motivo && <span style={{ color: '#98a2b3' }}> · {t.motivo}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Ações */}
        <div className="fc-drawer-foot">
          {id && (
            <button className="pm-btn-danger" onClick={() => setDeleteOpen(true)} disabled={saving}>
              Excluir
            </button>
          )}
          {(isEdit || id) && (
            <div className="pm-footer-value">
              <span className="pm-footer-value-label">Valor do negócio</span>
              <span className="pm-footer-value-num">{fmtBRL(itemsTotal)}</span>
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
            <button className="fc-ctrl" onClick={closeSmart}>
              {id ? 'Fechar' : 'Cancelar'}
            </button>
            <button className="fc-ctrl primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando…' : id ? 'Salvar' : 'Criar negócio'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title="Apagar negócio"
        message={`Apagar o negócio "${titulo || 'Sem título'}"? Os leads deixam de estar vinculados a ele. Esta ação não pode ser desfeita.`}
        confirmLabel="Apagar"
        danger
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setDeleteOpen(false) }}
      />
    </div>
  )
}
